<template>
  <!-- The channel pill and the quick-action chips are STATIC mockups: no handlers, no data
       source. They are meant to look real and do nothing. -->
  <section class="v2026-cards" :class="{ 'v2026-cards--compact': compact }">
    <div v-if="conversations && conversations.length > 0">
      <!-- key = id ไม่ใช่ index: ลำดับขยับทุกข้อความลูกค้า ถ้า key ตามตำแหน่ง state ของการ์ด (badge, avatar, countdown) จะติดช่องแทนติดบทสนทนา -->
      <div v-for="conversation in conversations" :key="conversation.id" class="v2026-card-wrap">
        <div
          role="button"
          class="v2026-card"
          :class="{
            'v2026-card--railed': allowPermission,
            'v2026-card--active': isActiveConversation(conversation),
            'v2026-card--stealth-active': isStealthActive(conversation),
            'v2026-card--unread': checkUnread(conversation),
            'v2026-card--cd-host': (useReminderCountdown && !!conversation.remind_at) || (useStatusIconPicker && hasFireStatus(conversation))
          }"
          @click.prevent="clickItem(conversation)"
        >
          <!-- THE CONTENT ZONE - every child except the rail, so the card has two INDEPENDENT hover
               zones. .v2026-card-rail is a SIBLING and must never move inside it. The children below
               are deliberately NOT re-indented, so the other rounds stay separable by hunk. -->
          <div class="v2026-card-content">
          <!-- Vue 2 has no teleport, so the banner and the bell are two mounts of the SAME
               component, each rendering only its own mode. -->
          <ReminderCountdownV2026
            v-if="useReminderCountdown && conversation.remind_at"
            mode="banner"
            :remindAt="conversation.remind_at"
          />

          <!-- Decoration only — no body tint, no border change. When a card is both counting
               down and urgent the countdown watermark wins; the CSS enforces that. -->
          <span class="v2026-si-skin"
                v-if="useStatusIconPicker && hasFireStatus(conversation)"
                aria-hidden="true">
            <span class="v2026-si-watermark"></span>
          </span>

          <div class="v2026-card-row1">
            <div class="v2026-card-avatar">
              <div class="image-cropper-md fade-in">
                <img
                  v-lazy="conversation.customer.profile_picture"
                  class="image-profile img-circle"
                  v-if="conversation.customer"
                />
              </div>
              <!-- Channel logo pinned to the avatar's corner (outside the cropper, whose overflow clips). -->
              <span
                v-if="useAvatarChannelBadge && channelPill(conversation) && channelPill(conversation).icon"
                class="v2026-card-channel-badge"
                :title="channelPill(conversation).label"
              >
                <img class="v2026-card-channel-badge__img" :src="channelPill(conversation).icon" :alt="channelPill(conversation).label" />
              </span>
              <span
                v-if="conversation.customer && conversation.customer.beneat_order_qty"
                v-html="$options.filters.vipStatus(conversation.customer.beneat_order_qty, 20)"
              ></span>
              <div class="mt-1" v-if="conversation.is_emergency">
                <span class="badge badge-danger">ด่วน!</span>
              </div>
            </div>
            <div class="v2026-card-headline">
              <div class="v2026-card-name-line">
                <template v-if="conversation.customer">
                  <template
                    v-if="conversation.customer_type === 'beneat_professional' && conversation.customer.beneat_professional"
                  >
                    <span class="badge badge-danger" v-if="conversation.customer.beneat_professional.status === 0">โดนปลด</span>
                    <span class="badge badge-secondary" v-else-if="conversation.customer.beneat_professional.professional_group_id === 0">0</span>
                    <span class="badge badge-success" v-else-if="conversation.customer.beneat_professional.professional_group_id === 1">A</span>
                    <span class="badge badge-warning" v-else-if="conversation.customer.beneat_professional.professional_group_id === 2">B</span>
                    <span class="badge badge-warning" v-else-if="conversation.customer.beneat_professional.professional_group_id === 3">C</span>
                    <span class="badge badge-primary" v-else-if="conversation.customer.beneat_professional.professional_group_id === 4">H</span>
                  </template>
                  <!-- title is compact-only (null = no attribute), so normal cards keep today's DOM. Raw name, not the shortText output. -->
                  <!-- Status icons at the front of the name — where admins used to type emoji into names in
                       the legacy list. Replaces the icon row below (useStatusIconsBeforeName). -->
                  <span class="v2026-card-name" :title="compact ? conversation.customer.name : null"><template v-if="useStatusIconPicker && useStatusIconsBeforeName"><span v-for="item in statusIconsFor(conversation)" :key="item.id" class="v2026-si-mark v2026-si-mark--inline" :style="{ color: item.color }" :title="item.label"><span v-if="item.badgeText" class="v2026-si-mark__badge"><span class="v2026-si-mark__badge-t">{{ item.badgeText }}</span></span><template v-else><i v-for="n in (item.repeat || 1)" :key="n" :class="item.fa" aria-hidden="true"></i></template></span></template>{{ conversation.customer.name | shortText(30) }}</span>
                </template>
              </div>
              <div class="v2026-card-subline" v-if="cardProvince(conversation) || hasWaitingTimer(conversation) || hasLastMessageTimer(conversation)">
                <!-- No province renders NOTHING - deliberate divergence from CustomerPanelV2026.vue's '-'. -->
                <span
                  class="v2026-card-province"
                  v-if="cardProvince(conversation)"
                  :title="cardProvince(conversation)"
                >{{ cardProvince(conversation) }}</span>
                <span
                  class="v2026-card-waiting"
                  v-if="hasWaitingTimer(conversation)"
                  title="รอมาตั้งแต่ลูกค้าทักเข้ามาครั้งแรก"
                >
                  <span class="v2026-card-waiting-label">รอ:</span>
                  <span class="v2026-card-waiting-time">{{ getWaitingDuration(conversation.start_conversation_at, now) }}</span>
                </span>
                <!-- ข้อความล่าสุดไม่ว่าใครส่ง (ไม่นับ event ของระบบ) — ถาดขาเข้าเรียงแบบ FIFO ตัวเลขนี้จึงไม่ไล่ลำดับที่นั่น -->
                <span
                  class="v2026-card-waiting"
                  v-if="hasLastMessageTimer(conversation)"
                  title="ข้อความล่าสุดของแชทนี้ (ลูกค้า / แอดมิน / bot)"
                >
                  <span class="v2026-card-waiting-label v2026-card-waiting-label--inbound">ล่าสุด:</span>
                  <span class="v2026-card-waiting-time v2026-card-waiting-time--inbound">{{ getWaitingDuration(lastMessageAt(conversation), now) }}</span>
                </span>
              </div>
              <div class="v2026-si-row" v-if="useStatusIconPicker && !useStatusIconsBeforeName && statusIconsFor(conversation).length > 0">
                <span v-for="item in statusIconsFor(conversation)"
                      :key="item.id"
                      class="v2026-si-mark"
                      :style="{ color: item.color }"
                      :title="item.label">
                  <span v-if="item.badgeText" class="v2026-si-mark__badge">
                    <span class="v2026-si-mark__badge-t">{{ item.badgeText }}</span>
                  </span>
                  <template v-else><i v-for="n in (item.repeat || 1)" :key="n" :class="item.fa" aria-hidden="true"></i></template>
                </span>
              </div>
            </div>
            <div class="v2026-card-metacol">
              <!-- เฉพาะเคสที่ปิดแล้ว (แทน "ล่าสุด:" ที่ซ่อนไปตอนปิดเคส): เวลาข้อความสุดท้ายก่อนปิด — last_message_at ไม่นับ <Closed> -->
              <div v-if="conversation.is_archived && lastMessageAt(conversation)" class="v2026-card-meta">
                <span class="v2026-card-time" title="ข้อความสุดท้าย">{{ lastMessageAt(conversation) | chatDateTime }}</span>
                <span class="badge badge-danger v2026-card-badge" v-if="!useCardUnreadBadge && checkUnreadCount(conversation)">
                  {{ conversation.unread_count }}
                </span>
              </div>
              <!-- The CASE OWNER (conversation.admin), not the watching avatar at the card's foot.
                   v-if, never v-show: a hidden 32px box would add height to every card. -->
              <img
                v-if="cardOwnerAdmin(conversation)"
                class="rounded-circle img-circle-sm v2026-card-owner"
                :src="cardOwnerAvatar(conversation)"
                :alt="cardOwnerTooltip(conversation)"
                v-b-tooltip.hover
                :title="cardOwnerTooltip(conversation)"
                @error="onOwnerAvatarError"
              />
            </div>
          </div>

          <!-- With the logo on the avatar, a card with no last message has nothing left on this line,
               so the line (and its 12px top margin) is skipped. -->
          <div class="v2026-card-preview-line"
               v-if="!useAvatarChannelBadge || conversation.last_message || (channelPill(conversation) && !channelPill(conversation).icon)">
            <template v-if="channelPill(conversation) && !(useAvatarChannelBadge && channelPill(conversation).icon)">
              <img
                v-if="channelPill(conversation).icon"
                class="v2026-channel-logo"
                :src="channelPill(conversation).icon"
                alt=""
              />
              <span
                v-else
                class="v2026-pill"
                :class="channelPill(conversation).cls"
              >{{ channelPill(conversation).label }}</span>
            </template>
            <span
              class="v2026-card-preview"
              :class="checkUnread(conversation) ? 'v2026-card-preview--unread' : ''"
            >
              <span v-if="conversation.last_message">{{ conversation.last_message.message | shortText(50) }}</span>
            </span>
          </div>

          <!-- useCardTagChips ON: one data-driven chip row. The OFF twin of this flag is on
               .v2026-card-actions below; the reminder mounts sit between them, so the two blocks
               cannot share one v-if/v-else pair. Flip both or neither. -->
          <!-- useFitTagChips ON: as many chips as the row's width allows, then +N with a hover card
               listing every tag (CardTagRowV2026). OFF = the fixed CARD_TAG_LIMIT row below. -->
          <template v-if="useCardTagChips && useFitTagChips">
            <CardTagRowV2026
              class="v2026-card-tags"
              v-if="conversation.is_archived || cardTagItems(conversation).length > 0"
              :items="cardTagItems(conversation)"
              :archived="!!conversation.is_archived"
              :resolvedIcon="resolvedIcon"
              :compact="compact"
            />
          </template>
          <template v-else-if="useCardTagChips">
            <div
              class="v2026-card-tags v2026-card-tags--chips"
              v-if="conversation.is_archived || cardTagChips(conversation).length > 0"
            >
              <!-- Legacy Resolved colour kept literal; the color prop drives border, label and glyph. -->
              <ActionChipV2026
                class="v2026-card-resolvedchip"
                v-if="conversation.is_archived"
                :color="'#007bff'"
                :icon="resolvedIcon"
              >Resolved</ActionChipV2026>
              <!-- No click/keydown/focus anywhere in this row: the whole card is the click target
                   and a nested handler would swallow it. -->
              <ActionChipV2026
                v-for="chip in cardTagChips(conversation)"
                :key="chip.key"
                class="v2026-card-tagchip"
                :color="chip.color"
                :fill="chip.fill"
                :icon="chip.icon"
                :title="chip.label"
              >
                <span class="v2026-card-tagchip__t">{{ chip.label }}</span>
              </ActionChipV2026>
              <span
                class="v2026-card-tagmore"
                v-if="cardTagOverflowNames(conversation).length"
                :title="cardTagOverflowNames(conversation).join('\n')"
              >+{{ cardTagOverflowNames(conversation).length }}</span>
            </div>
          </template>
          <!-- Rollback path: useCardTagChips = false restores this row verbatim, including the
               quirk that `tags: []` is truthy so the empty div still renders (and still spends
               its 10px top margin). Do not "fix" that here — it is the parity baseline. -->
          <template v-else>
            <div class="v2026-card-tags" v-if="conversation.is_archived || conversation.tags">
              <span class="v2026-resolved-chip" v-if="conversation.is_archived">Resolved</span>
              <template v-if="conversation.tags">
                <template v-if="conversation.chat_topic">
                  <span
                    class="badge text-white mr-1"
                    style="color: white"
                    :style="{ background: conversation.chat_topic.color || '#000' }"
                  >{{ displayChatTopic(conversation.chat_topic) }}</span>
                </template>
                <template v-for="(tag, tIndex) in conversation.tags">
                  <span
                    v-if="tag.system"
                    class="badge text-white mr-1"
                    :style="'background-color:' + tag.color"
                    :key="'s' + tIndex"
                  >{{ tag.name }}</span>
                </template>
                <template v-for="(tag, tIndex) in conversation.tags">
                  <span
                    v-if="!tag.system"
                    class="badge text-white mr-1"
                    :style="'background-color:' + tag.color"
                    :key="'n' + tIndex"
                  >{{ tag.name }}</span>
                </template>
              </template>
            </div>
          </template>

          <ReminderCountdownV2026
            v-if="useReminderCountdown && conversation.remind_at"
            mode="bell"
            :remindAt="conversation.remind_at"
          />
          <div class="v2026-card-reminder" v-if="!useReminderCountdown && conversation.remind_at" v-b-tooltip.hover title="บทสนทนานี้มีการแจ้งเตือน">
            <i class="fas fa-bell"></i>
            <span>{{ conversation.remind_at | prettyOnlyTime }}</span>
          </div>

          <!-- Static mock chips, kept only for the rollback path; see the useCardTagChips row above. -->
          <div class="v2026-card-actions" v-if="!useCardTagChips">
            <action-chip-v2026 label="จองใช้บริการ" />
            <action-chip-v2026 label="เปลี่ยนวันเวลา" />
            <action-chip-v2026 op badge="OP" label="ใบกำกับภาษี" />
          </div>

          <div
            class="v2026-card-watching"
            v-if="conversation.conversation_admins && conversation.conversation_admins.length > 0"
          >
            <template v-for="(item, wIndex) in conversation.conversation_admins">
              <img
                v-if="item.status === 1"
                :key="wIndex"
                :src="item.profile_picture"
                v-b-tooltip.hover
                :title="item.name + ' ดูอยู่'"
                class="rounded-circle img-circle-xs admin-watching"
              />
            </template>
          </div>

          </div>

          <!-- จำนวนข้อความที่ยังไม่ได้อ่าน. ลอยทับคอลัมน์ incognito ที่มุมขวาบน มีหน้าที่แสดงตัวเลขอย่างเดียว:
               pointer-events: none ทำให้คลิกทะลุลงไปโดนสิ่งที่อยู่ข้างใต้เสมอ (มีสิทธิ์ = rail -> โหมดไม่ระบุ
               ตัวตน, ไม่มีสิทธิ์ = การ์ด -> เปิดแชทตามปกติ) จึงวางทับได้โดยไม่กินคลิกของใคร.
               อยู่ก่อน .v2026-card-rail เสมอ เพื่อให้ rail ยังเป็น child ตัวสุดท้ายตามที่ CSS ข้างล่างพึ่งพา. -->
          <span
            class="v2026-card-count"
            v-if="useCardUnreadBadge && checkUnreadCount(conversation)"
            :aria-label="conversation.unread_count + ' ข้อความที่ยังไม่ได้อ่าน'"
          >{{ unreadCountLabel(conversation) }}</span>

          <!-- FULL-HEIGHT right rail. The WHOLE column is the incognito button, so the handler lives
               here; .stop is mandatory or the click also opens the conversation behind the incognito
               view. Must stay a DIRECT child of .v2026-card and a SIBLING of .v2026-card-content, and
               last in DOM order - every --railed / --stealth-active / :has() selector depends on it. -->
          <div
            class="v2026-card-rail"
            v-if="allowPermission"
            @click.stop.prevent="modalUnread(conversation)"
          >
            <span
              v-if="allowPermission"
              class="v2026-card-stealth"
              v-b-tooltip.hover
              title="อ่านแบบไม่ระบุตัวตน"
            >
              <span class="v2026-card-stealth-mask" aria-hidden="true"
                    :style="{ '-webkit-mask-image': 'url(' + stealthIcon + ')', 'mask-image': 'url(' + stealthIcon + ')' }"></span>
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="text-center py-4" v-else-if="loading">
      <LoadingV2026 label="กำลังโหลด" />
    </div>
    <div class="text-center py-4" v-else>
      <label>ไม่พบบทสนทนา</label>
    </div>
    <ModalListChat ref="refStealthRead"/>
  </section>
</template>

<script>
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
import ModalListChat from '@/components/coversation/components/ModalListChat.vue'
import ActionChipV2026 from '@/components/coversation/ActionChipV2026'
import { resolveTagChipFill } from '@/components/coversation/tagChipFill'
import ReminderCountdownV2026 from '@/components/coversation/ReminderCountdownV2026'
import CardTagRowV2026 from '@/components/coversation/CardTagRowV2026.vue'
import LoadingV2026 from '@/components/coversation/LoadingV2026.vue'
import { resolveStatusIcons, hasWatermarkStatus } from '@/components/coversation/statusIconCatalog'

// Visible chips in the card tag row before the +N pill takes over. One knob, one line.
const CARD_TAG_LIMIT = 3
// Compact cards: one visible slot. Resolved takes it on archived cards (see cardTagLimit).
const CARD_TAG_LIMIT_COMPACT = 1
// CUSTOMER RAIL ONLY - the switch for which customer source the card renders.
const CARD_PROVINCE_SOURCE = 'latest_order'
// MAID RAIL. NOT A SWITCH - just the member name that rail's single source arrives under.
const CARD_PROVINCE_SOURCE_PROFESSIONAL = 'professional'
// Case-owner avatar fallback. v2026 house style.
const CARD_ADMIN_FALLBACK_AVATAR = require('@/assets/no_photos.png')
// Always an explicit url: icon="" would invalidate the mask and paint a solid square.
const CARD_ICON_TAG = require('@/assets/icons2026/tag.svg')
const CARD_ICON_TOPIC = require('@/assets/icons2026/topic.svg')
const CARD_ICON_RESOLVED = require('@/assets/icons2026/closed-case.svg')

export default {
  name: 'chat-card-v2026',
  props: {
    conversations: Array,
    loading: { type: Boolean, default: false },
    selectedConversation: Object,
    // TRUE = the incognito trigger opens the inline read view instead of the modal.
    inlineStealthRead: {
      type: Boolean,
      default: false
    },
    // Incognito never sets selectedConversation, so this drives the "being read" highlight.
    stealthActiveConversation: {
      type: Object,
      default: null
    },
    // TRUE = compact layout; the page measures the list column. FALSE renders today's card unchanged.
    compact: {
      type: Boolean,
      default: false
    }
  },
  components: {
    ModalListChat,
    ActionChipV2026,
    ReminderCountdownV2026,
    CardTagRowV2026,
    LoadingV2026
  },
  data () {
    return {
      // FALSE restores today's static bell; the original markup is still in the template.
      useReminderCountdown: true,
      // Twin of the flag in CustomerInformationV2026 (the picker half); flipped together by hand.
      useStatusIconPicker: true,
      // FALSE restores today's bootstrap tag badges + the three static mock chips; both are kept verbatim below.
      useCardTagChips: true,
      // FALSE restores the fixed CARD_TAG_LIMIT chips + a title-only +N (the template's next branch).
      useFitTagChips: true,
      // FALSE puts the channel logo back at the start of the preview line.
      useAvatarChannelBadge: true,
      // FALSE = กลับไปพฤติกรรมเดิม: ตัวเลขข้อความใหม่ขึ้นเฉพาะการ์ดของเคสที่ปิดแล้ว (branch ใน .v2026-card-metacol)
      useCardUnreadBadge: true,
      stealthIcon: require('@/assets/icons2026/incognito.svg'),
      // Template access to the module constant; the mask url must never resolve to ''.
      resolvedIcon: CARD_ICON_RESOLVED,
      // FALSE moves the status icons back to their own row under the name.
      useStatusIconsBeforeName: true
    }
  },
  watch: {
    'selectedConversation.id': function (newVal, oldVal) {
      if (oldVal) {
        const submitData = {
          id: oldVal,
          is_read: 1
        }
        this.updateConversation(submitData)
        this.$store.commit('conversation/SET_IS_READ', oldVal)
      }
    }
  },
  computed: {
    ...mapState('ticker', ['now']),
    ...mapState('conversation', ['customerType']),
    ...mapState('admin', ['allowPermission'])
  },
  methods: {
    ...mapActions('ticker', ['startTicker']),
    ...mapActions('conversation', ['updateConversation']),
    ...mapActions('admin', ['checkPermissions']),
    isActiveConversation (conversation) {
      // While inline incognito-read is active, suppress the normal highlight so only one card lights up.
      if (this.stealthActiveConversation) {
        return false
      }
      return this.selectedConversation && this.selectedConversation.id === conversation.id
    },
    isStealthActive (conversation) {
      return !!(this.stealthActiveConversation && this.stealthActiveConversation.id === conversation.id)
    },
    conversationCss (conversation) {
      let css = ''
      if (
        this.selectedConversation &&
        this.selectedConversation.id === conversation.id
      ) {
        css = 'active-conversation'
      } else if (!conversation.is_read || conversation.unread_count >= 1) {
        css = 'new-conversation'
      }
      return css
    },
    // Takes the CONVERSATION, never the list index, so the province travels with the row across a re-sort.
    cardProvince (conversation) {
      const sources = conversation && conversation.province_sources
      // EXACT equality, never startsWith / includes: the bare `professional` ENUM is the APPLICANT rail.
      const source = conversation && conversation.customer_type === 'beneat_professional'
        ? CARD_PROVINCE_SOURCE_PROFESSIONAL
        : CARD_PROVINCE_SOURCE
      const name = sources ? sources[source] : null
      // Trim first: a whitespace-only value renders an invisible span that still eats the gap.
      const text = typeof name === 'string' ? name.trim() : ''
      return text || null
    },
    // Extracted verbatim so the subline container and the timer inside it can never drift apart.
    hasWaitingTimer (conversation) {
      return !!(conversation.start_conversation_at && !conversation.admin && !conversation.is_archived)
    },
    // last_message_at = ข้อความล่าสุดไม่ว่าใครส่ง (server คำนวณ ดู LAST_MESSAGE_AT_SQL)
    // ถอยไป last_active_at (ลูกค้าส่งข้อความเข้ามาล่าสุด) เมื่อแถวมาจากทางที่ไม่ได้แนบค่านี้
    lastMessageAt (conversation) {
      return conversation.last_message_at || conversation.last_active_at || null
    },
    // แสดงทั้งเคสที่มีและไม่มีเจ้าของ
    hasLastMessageTimer (conversation) {
      return !!(this.lastMessageAt(conversation) && !conversation.is_archived)
    },
    cardOwnerAdmin (conversation) {
      if (!conversation) { return null }
      return conversation.admin || null
    },
    cardOwnerAvatar (conversation) {
      const admin = this.cardOwnerAdmin(conversation)
      // Trim first: a whitespace-only url still counts as a string and would paint a broken image.
      const src = admin && typeof admin.profile_picture === 'string' ? admin.profile_picture.trim() : ''
      return src || CARD_ADMIN_FALLBACK_AVATAR
    },
    // `รับเคส` is the application's own term for this field.
    cardOwnerTooltip (conversation) {
      const admin = this.cardOwnerAdmin(conversation)
      const name = admin && typeof admin.name === 'string' ? admin.name.trim() : ''
      return name ? name + ' รับเคสนี้' : 'แอดมินรับเคส'
    },
    // One-shot: the flag stops a loop if the placeholder itself ever fails.
    onOwnerAvatarError (event) {
      const el = event && event.target
      if (!el || el.dataset.ownerFallback === '1') { return }
      el.dataset.ownerFallback = '1'
      el.src = CARD_ADMIN_FALLBACK_AVATAR
    },
    // The ONLY source of the ids the card renders. `|| []` is mandatory, not defensive: a
    // redis-cached conversation written before the column existed simply has no such key.
    // Deliberately NOT gated by customer_type — hiding saved data would make it vanish silently.
    statusIconIds (conversation) {
      return Array.isArray(conversation && conversation.status_icons) ? conversation.status_icons : []
    },
    statusIconsFor (conversation) {
      return resolveStatusIcons(this.statusIconIds(conversation))
    },
    // Only statuses flagged `watermark` in the catalogue get the large treatment — today, the flame.
    hasFireStatus (conversation) {
      return hasWatermarkStatus(this.statusIconIds(conversation))
    },
    // Everything the merged tag row can show, in row order: chat topic, then system tags, then
    // the rest, each partition keeping its payload order. A partition, not a sort — a comparator
    // would invite a "sort by name" edit that breaks parity with the right panel.
    // The topic is prepended whether or not `tags` is an array: today it is nested inside
    // v-if="conversation.tags" and can never render when tags is null.
    cardTagItems (conversation) {
      const tags = Array.isArray(conversation && conversation.tags) ? conversation.tags : []
      const items = []
      if (conversation && conversation.chat_topic) {
        items.push({
          key: 'topic',
          label: this.displayChatTopic(conversation.chat_topic),
          // '' is the prop default and falls back to the teal outline; null must never reach the style.
          color: conversation.chat_topic.color || '',
          // null for '' / non-hex, so a colourless topic stays the teal outline. No '#000' default.
          fill: resolveTagChipFill(conversation.chat_topic.color),
          icon: CARD_ICON_TOPIC
        })
      }
      const toChip = (tag) => ({
        key: 't' + tag.id,
        label: tag.name,
        color: tag.color || '',
        fill: resolveTagChipFill(tag.color),
        icon: CARD_ICON_TAG
      })
      tags.filter(tag => tag.system).forEach(tag => items.push(toChip(tag)))
      tags.filter(tag => !tag.system).forEach(tag => items.push(toChip(tag)))
      return items
    },
    // Normal: always CARD_TAG_LIMIT. Compact: the Resolved chip (not a tag item) takes the single
    // slot, so an archived card shows [Resolved] [+N] with N = every tag item.
    cardTagLimit (conversation) {
      if (!this.compact) { return CARD_TAG_LIMIT }
      return conversation && conversation.is_archived ? 0 : CARD_TAG_LIMIT_COMPACT
    },
    cardTagChips (conversation) {
      return this.cardTagItems(conversation).slice(0, this.cardTagLimit(conversation))
    },
    cardTagOverflowNames (conversation) {
      return this.cardTagItems(conversation).slice(this.cardTagLimit(conversation)).map(item => item.label)
    },
    displayChatTopic (chatTopic) {
      if (chatTopic.parent_id !== 0 && chatTopic.parent_chat) {
        return `${chatTopic.parent_chat.name} - ${chatTopic.name}`
      } else {
        return `${chatTopic.name}`
      }
    },
    checkUnread (conversation) {
      let result = false
      if (this.selectedConversation) {
        if (
          this.selectedConversation.id !== conversation.id &&
          !conversation.is_read
        ) {
          result = true
        }
      } else {
        if (!conversation.is_read) {
          result = true
        }
      }
      return result
    },
    checkUnreadCount (conversation) {
      let result = false
      if (this.selectedConversation) {
        if (
          this.selectedConversation.id !== conversation.id &&
          conversation.unread_count &&
          !conversation.is_read
        ) {
          result = true
        }
      } else {
        if (conversation.unread_count) {
          result = true
        }
      }
      return result
    },
    // cap ที่ 99+ เพราะ unread_count รีเซ็ตเฉพาะตอนเปิดแชท (GET /conversations/:id) เคสเก่าที่ไม่มีใคร
    // เปิดจึงสะสมได้ถึงสี่หลัก ซึ่งจะดันความกว้าง badge ทับเนื้อการ์ด
    unreadCountLabel (conversation) {
      const count = Number(conversation && conversation.unread_count) || 0
      return count > 99 ? '99+' : String(count)
    },
    getWaitingDuration (start) {
      let seconds = moment().diff(moment(start), 'seconds')
      // Returns ONLY the numeric time; the gray label lives in the template so the time reads red.
      return this.$options.filters.relativeTimeSpanTicker(seconds)
    },
    channelPill (conversation) {
      switch (conversation.type) {
        case 'PrivateConversation':
          return { label: 'Messenger', cls: 'v2026-pill--fb', icon: require('@/assets/icons2026/messenger.svg') }
        case 'PublicConversation':
          return { label: 'Facebook', cls: 'v2026-pill--fb', icon: require('@/assets/icons2026/facebook-color.svg') }
        case 'LineAtConversation':
        case 'LineProfessionalConversation':
          return { label: 'LINE', cls: 'v2026-pill--line', icon: require('@/assets/icons2026/line-color.svg') }
        default:
          return null
      }
    },
    clickItem (conversation) {
      // "รับเคสแล้ว" เป็นส่วนหนึ่งของ "กำลังให้บริการ" (server คืน active_menu = 'active') —
      // เปิดดูจากเมนูนี้ต้องอยู่หน้าเดิม ไม่เด้งไปรายการของทุกคน
      const staysInMyInbox = this.$store.state.menu.type === 'my_inbox' && conversation.active_menu === 'active'
      if (conversation.active_menu && !staysInMyInbox) {
        this.$store.commit('menu/SET_TYPE_MENU', conversation.active_menu)
      }
      this.$emit('onItemClick', conversation)
    },
    modalUnread (conversation) {
      // Emits up to the page, which fetches read-only and never sets selectedConversation —
      // that is what preserves the "ไม่ระบุตัวตน" semantics.
      if (this.inlineStealthRead) {
        this.$emit('onStealthRead', conversation)
        return
      }
      this.$refs.refStealthRead.openModal(conversation)
    }
  },
  created () {
    this.startTicker()
    this.checkPermissions('/stealth-mode-reading-chat')
  }
}
</script>

<style scoped>
.v2026-cards {
  padding: 6px;
}

.v2026-card-wrap {
  margin-bottom: 8px;
}

.v2026-card {
  position: relative;
  background: #ffffff;
  border: 1px solid #eaecef;
  border-radius: 12px;
  padding: 15px;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.15s ease;
}

.v2026-card:hover {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

/* Load-bearing, NOT dead code: the box-shadow LIFT is the one hover property that cannot move onto
   .v2026-card-content (a shadow declared there is cast from the card's padding box and paints over
   the card's own border), so this stays the only thing that un-matches the lift over the rail.
   Same (0,2,0) as .v2026-card:hover - it wins only by coming later. Do not reorder. */
.v2026-card:has(.v2026-card-rail:hover) {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);   /* = the resting shadow */
}

/* THE CONTENT ZONE. Exists only because .v2026-card:hover also fires when the pointer is on the rail
   (a DESCENDANT of the card), so the cyan hover cannot live on the card itself.
   The negative margins bleed this box out to the card's PADDING BOX and the paddings put the layout
   back byte-for-byte, so the card's 15px padding is not a hover dead ring. The right pair uses
   --v2026-rail-gap, an identity in --v2026-rail-reserve, so its right edge lands on the rail's left
   edge at every rail width and the @media block needs no companion rule here.
   DO NOT add position:relative, z-index, isolation or overflow:hidden: relative would steal
   .v2026-si-skin / .v2026-cd-skin's containing block (MUST stay .v2026-card) and overflow would clip
   .v2026-cd-strip's full-bleed margins. */
.v2026-card-content {
  margin: -15px;
  margin-right: calc(-1 * var(--v2026-rail-gap, 15px));
  padding: 15px;
  padding-right: var(--v2026-rail-gap, 15px);
  border-radius: 11px;                       /* = the card's 12px minus its 1px border */
  transition: background-color 0.12s ease;   /* the rail's own curve, so both zones feel identical */
}

/* Railed cards: the right edge is the rail's hairline, not a card corner - no radius there. */
.v2026-card--railed .v2026-card-content {
  border-radius: 11px 0 0 11px;
}

/* 16%, against the rail's 8%: alpha is a function of the TINT's luminance, not taste - #31C1D7 is a
   light hue and the rail's #48484A is dark, so 2x alpha lands both zones on the same perceptual step.
   Press is 2x hover in both. Literals because the token family has no 16% member.
   THE CYAN IS THE var() FALLBACK, not this rule's only value: the .v2026-cd-skin rule below swaps
   both vars for the countdown card's own pink, and an engine without :has() renders this cyan. */
.v2026-card-content:hover  { background: var(--v2026-content-hover, rgba(49, 193, 215, 0.16)); }
.v2026-card-content:active { background: var(--v2026-content-press, rgba(49, 193, 215, 0.32)); }

/* Unread tint. Declared BEFORE the two selected states so those always win on
   equal specificity — checkUnread() already excludes the selected card, this is
   belt-and-braces. Same grey as --stealth-active by design decision. */
.v2026-card--unread {
  background: rgba(153, 153, 153, 0.12);
}

.v2026-card--active {
  background: var(--v2026-color-main-soft);
  border-color: var(--v2026-color-main);
}

.v2026-card--active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: 0px 12px 12px 0px;
  background: var(--v2026-color-main-bar);
  margin: 10px 0;
}

.v2026-card--stealth-active {
  background: rgba(153, 153, 153, 0.12);
  border-color: #999999;
}

.v2026-card--stealth-active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: 0px 12px 12px 0px;
  background: #999999;
  margin: 10px 0;
}

.v2026-card--stealth-active .v2026-card-stealth {
  background: #999999;
  border-color: white;
}

/* The 2nd selector is a specificity guard over the rail's (0,3,0) hover. Delete it and the glyph goes
   #48484A on #999999 - a dark grey that looks intentional instead of failing loudly. */
.v2026-card--stealth-active .v2026-card-stealth-mask,
.v2026-card--stealth-active .v2026-card-rail:hover .v2026-card-stealth-mask {
  background-color: #ffffff;
}

.v2026-card--cd-host {
  isolation: isolate;
}

/* --- pink x selected: they COEXIST. Pink owns the body pigment and, by geometry, the border ring;
       the LEFT BAR owns "this conversation is open". The four rules below interlock - change one
       alone and the set breaks. Hook is :has(.v2026-cd-skin), NEVER .v2026-card--cd-host, which
       hasFireStatus also sets and which would turn fire cards pink. The border needs no rule of its
       own: .v2026-cd-skin is inset -1px and an absolute offset resolves from the PADDING box. */

/* Neutral grey, never the selected cyan: cyan 8% under the skin's pink 8% measures out grey-lavender
   - the hue dies and the card stops reading as a reminder. Same literal as the two rules above. */
.v2026-card--active:has(.v2026-cd-skin) {
  background: rgba(153, 153, 153, 0.12);
}

/* This bar is what says "this conversation is open"; 5px MUST match the strip cap's border-left
   below or the two stop reading as one rail. Radius NOT touched - it is cyan-on-cyan in the overlap. */
.v2026-card--active:has(.v2026-cd-skin)::before,
.v2026-card--stealth-active:has(.v2026-cd-skin)::before {
  width: 5px;
}

/* The ::before bar's 10px margin leaves the banner with no "open" signal; this continues it through.
   padding-left 7px (= 12 - 5) keeps the border-box strip 30px tall with the glyph on its current x. */
.v2026-card--active:has(.v2026-cd-skin) ::v-deep .v2026-cd-strip {
  border-left: 5px solid var(--v2026-color-main-bar);
  padding-left: 7px;
}

.v2026-card--stealth-active:has(.v2026-cd-skin) ::v-deep .v2026-cd-strip {
  border-left: 5px solid #999999;
  padding-left: 7px;
}

/* The countdown card's content hover is PINK, not cyan: hovering must DEEPEN the card's own pink, not
   rotate its hue to blue-grey. --v2026-color-count-soft is the EXACT declaration the pink body is made
   of, so this just paints one more layer of it - no new colour, no new token.
   8/16, not the cyan's 16/32: #FF2D55 is a dark hue, and 16% here would ship a permanent press state.
   :has(.v2026-cd-skin), NOT .v2026-card--cd-host: --cd-host is ALSO set by hasFireStatus, so it would
   turn fire cards pink. Hook the hue to the pigment.
   No :not() gates it any more, deliberately: a selected reminder card is now fully pink too, so it
   needs the pink hover just as much. No :has() support => the var() fallbacks ship today's cyan. */
.v2026-card:has(.v2026-cd-skin) {
  --v2026-content-hover: var(--v2026-color-count-soft, #FF2D5514);   /* = #FF2D55 @ 7.84% */
  --v2026-content-press: rgba(255, 45, 85, 0.16);   /* = #FF2D55 @16% = 2x -soft */
}

.v2026-si-skin {
  position: absolute;
  top: -1px;
  right: -1px;
  bottom: -1px;
  left: -1px;
  z-index: -1;
  border-radius: 12px;
  overflow: hidden;
  pointer-events: none;
}

.v2026-si-watermark {
  position: absolute;
  top: 80%;
  right: 5px;
  width: 104px;
  height: 104px;
  margin-top: -52px;
  background: url('~@/assets/icons2026/fire-watermark.svg') center / contain no-repeat;
}

.v2026-card:has(.v2026-cd-skin) .v2026-si-skin {
  display: none;
}

.v2026-card--active .v2026-si-watermark,
.v2026-card--stealth-active .v2026-si-watermark {
  opacity: 0.5;
}

.v2026-card-row1 {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.v2026-card-avatar {
  position: relative;
  flex: 0 0 48px;
  width: 48px;
}

/* 11px + the 1px white ring = 13px, the size of the status icons (.v2026-si-mark font-size) in
   both card modes. Bottom-right of the 48px photo: top/left = photo size - badge size + 1px overhang. */
.v2026-card-channel-badge {
  position: absolute;
  top: 35px;
  left: 32px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 0 0 1px #ffffff, 0 1px 2px rgba(16, 24, 40, 0.18);
}

.v2026-card-channel-badge__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.v2026-card-avatar .image-cropper-md {
  width: 48px;
  height: 48px;
  overflow: hidden;
  border-radius: 50%;
}

.v2026-card-avatar .image-profile {
  width: 48px;
  height: 48px;
  object-fit: cover;
}

.v2026-card-headline {
  flex: 1 1 auto;
  min-width: 0;
}

.v2026-card-name-line {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
}

.v2026-card-name {
  font-size: 16px;
  font-weight: 700;
  color: #212529;
}

.v2026-card-province {
  font-size: 13px;
  color: #868e96;
  flex: 0 1 auto;
  min-width: 0;
  white-space: nowrap;
  /* Guard only - dormant on real names; stops a pathological value bleeding out of the row. */
  overflow: hidden;
  text-overflow: ellipsis;
}

.v2026-card-meta {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  /* The stealth button used to set this row's 32px height before it moved to the rail; without this
     floor every card without an owner would shrink by 14px. */
  min-height: 32px;
}

.v2026-card-time {
  font-size: 11px;
  color: #adb5bd;
}

.v2026-card-badge {
  background: var(--v2026-color-count);
  color: #ffffff;
  border-radius: 10px;
}

/* ตัวเลขข้อความใหม่ - ทรง notification badge เกาะมุมขวาบน ล้ำออกนอกการ์ดไป 6px (ประมาณ 1 ใน 3 ของตัวมัน)
   จึงอ่านเป็นของที่ "ลอยอยู่เหนือการ์ด" ไม่ใช่ของที่ถูกขอบโค้ง 12px ของการ์ดกัดแหว่ง. ส่วนที่ล้นตกอยู่ใน
   ช่องว่าง 8px ระหว่างการ์ด และใน padding 6px ของ .v2026-cards จึงไม่โดน scroll container ตัด และเพราะ
   การ์ดใบล่างวาดทับใบบนตามลำดับ DOM badge จึงไม่เคยถูกใบบนบัง.
   ring ขาว 2px คือตัวที่ทำให้มันดูลอย - แยก badge ออกจากเส้นขอบการ์ด จากพื้นชมพูของการ์ดที่ยังไม่ได้อ่าน
   และจากการ์ดใบบนตอนที่ล้นขึ้นไปทับ. เอาออกเมื่อไหร่ badge จะกลับไปดูเหมือนสติกเกอร์แปะทันที.
   จัดกลางด้วย flex ไม่ใช่ line-height: ความสูงคงที่ 22px แต่ตัวเลขจะ 1-3 หลักก็ยังอยู่กึ่งกลางเป๊ะ.
   pointer-events: none คือหัวใจของ "แสดงอย่างเดียว" - ห้ามเอาออก ไม่งั้น badge จะกินคลิกของ rail
   และ hover tint ของ rail จะกระพริบตอนเมาส์ผ่าน badge. */
.v2026-card-count {
  position: absolute;
  top: -6px;
  right: -6px;
  z-index: 3;
  pointer-events: none;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--v2026-color-count);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.01em;
  font-variant-numeric: tabular-nums;   /* เลขทุกหลักกว้างเท่ากัน badge จึงไม่กระตุกตอนนับเพิ่ม */
  white-space: nowrap;
  box-shadow: 0 0 0 2px #ffffff, 0 2px 6px rgba(255, 45, 85, 0.35);
}

.v2026-card-stealth {
  color: var(--v2026-color-icon-idle);
  cursor: pointer;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  /* The incognito family, not the brand cyan: cyan-8% was byte-identical to .v2026-card--active's own
     background, so on a selected card the pill's chip shape was invisible. */
  background: var(--v2026-color-stealth-soft);
  border-radius: 8px;
  padding: 6px;
}

/* The strip and the icon are ONE control, so the pill has no hover of its own. Paired with the mask
   rule below so a future text child cannot come out the wrong colour. */
.v2026-card-rail:hover .v2026-card-stealth {
  color: var(--v2026-color-stealth);
}

.v2026-card-stealth-mask {
  display: inline-block;
  width: 20px;
  height: 20px;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: var(--v2026-color-icon-idle);
}

.v2026-card-rail:hover .v2026-card-stealth-mask {
  background-color: var(--v2026-color-stealth);
}

.v2026-card-subline {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  /* row 4px, column 8px - the column value is unchanged. */
  gap: 4px 8px;
  margin-top: 4px;
}

.v2026-card-waiting {
  font-size: 13px;
  /* The protected item: never shrinks, never truncates, never breaks internally. */
  flex: 0 0 auto;
  white-space: nowrap;
}

.v2026-card-waiting-label {
  color: #868e96;
  margin-right: 4px;
}

.v2026-card-waiting-time {
  color: #fa5252;
}

/* Not an SLA alarm - the sort key. Red stays reserved for "รอ:". */
.v2026-card-waiting-time--inbound {
  color: #007AFF;
}

/* "ล่าสุด:" is the only timer left in the meta row, so its label carries weight instead of grey.
   Its time is #007AFF, not --v2026-color-main: #31C1D7 on white is too light for 13px text. */
.v2026-card-waiting-label--inbound {
  color: var(--v2026-color-stealth, #48484A);
  font-weight: 600;
}

.v2026-si-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 5px;
}

.v2026-si-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
  font-size: 13px;
  line-height: 1;
}

.v2026-si-mark--inline {
  vertical-align: middle;
  margin-right: 3px;
  position: relative;
  top: -1px;
}

.v2026-si-mark__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 11px;
  padding: 0 3px;
  border-radius: 3px;
  background: currentColor;
}

.v2026-si-mark__badge-t {
  color: #ffffff;
  font-size: 8px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.2px;
}

.v2026-card-preview-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 0 60px;
}

.v2026-pill {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 600;
  color: #ffffff;
  padding: 2px 8px;
  border-radius: 4px;
}

.v2026-pill--line {
  background: #06c755;
}

.v2026-pill--fb {
  background: #1877f2;
}

.v2026-channel-logo {
  flex: 0 0 auto;
  height: 25px;
  width: auto;
  vertical-align: middle;
  margin-right: 4px;
}

.v2026-card-preview {
  font-size: 16px;
  font-weight: 500;
  color: #868e96;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v2026-card-preview--unread {
  color: #212529;
  font-weight: 700;
}

.v2026-card-tags {
  margin: 10px 0 0 60px;
}

.v2026-resolved-chip {
  display: inline-block;
  border: 1px solid #007bff;
  color: #007bff;
  border-radius: 4px;
  padding: 0 6px;
  font-size: 11px;
  margin-right: 4px;
}

/* useCardTagChips ON — the merged tag row. A modifier on .v2026-card-tags, which already
   supplies margin: 10px 0 0 60px, so the row does not move between flag states. */
.v2026-card-tags--chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

/* Fixed label, so no clamp: shrink: 0 makes overflow wrap the row instead of squashing the chip. */
.v2026-card-resolvedchip {
  flex: 0 0 auto;
}

/* Widest clamp that still fits two chips in the 255px row (2 x 120 + 8 gap = 248). */
.v2026-card-tagchip {
  max-width: 120px;
}

/* The glyph is a flex item and would shrink before the text does; pin it. */
::v-deep .v2026-card-tagchip .v2026-action-chip-ico {
  flex: 0 0 auto;
}

/* The ellipsis cannot live on the chip root: the label is an anonymous flex item there.
   This span is our slot content, so it is styleable. min-width: 0 is what lets a flex
   item shrink below its content width. */
.v2026-card-tagchip__t {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* +N counter. A plain span, never an ActionChipV2026: that always renders its icon span and
   an empty mask url paints a solid square. line-height 18px reproduces a chip's 30px height. */
.v2026-card-tagmore {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  font-size: 12px;
  line-height: 18px;
  color: #868e96;
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
  padding: 5px 10px;
}

.v2026-card-reminder {
  margin: 10px 0 0 60px;
  font-size: 11px;
  color: #6c757d;
}

.v2026-card-reminder i {
  margin-right: 4px;
}

.v2026-card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0 0 60px;
}

.v2026-card-watching {
  display: flex;
  flex-direction: row-reverse;
  margin-top: 10px;
}

.admin-watching {
  width: 25px;
  margin: 1px;
  border: 2px solid #e9e9e9;
}

/* Case-owner column, wrapping the EXISTING .v2026-card-meta so that rule stays untouched.
   STACKING, NOT INLINING: a column's width is its WIDEST ROW, so the customer name truncates exactly
   where it did before. Inside .v2026-card-meta the width would be a SUM. Do not simplify this away. */
.v2026-card-metacol {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  /* gap, never margin-top: a flex gap only exists BETWEEN rendered items, so a no-owner card
     collapses to exactly today's box. */
  gap: 6px;
}

/* Same construction as .admin-watching, two ranks up: 32px vs 25px, brand ring vs inert grey. */
.v2026-card-owner {
  flex: 0 0 auto;
  display: block;
  border: 2px solid var(--v2026-color-main);
}

/* Full-height right rail. Absolute, not a flex column: a flex column would break the countdown
   strip's full-bleed negative margins. --railed reserves the rail's column as card padding so no
   row's content box ever reaches it - the two MUST stay in lockstep, hence the vars. */
.v2026-card--railed {
  --v2026-rail-w: 50px;      /* hairline -> card inner edge: 1 border + 8 + 32 button + 9 */
  --v2026-rail-gap: 12px;    /* clear space between content and the hairline (= row1's old gap) */
  --v2026-rail-reserve: calc(var(--v2026-rail-w) + var(--v2026-rail-gap));
  /* Overrides the 15px from .v2026-card's shorthand: same specificity, later in the file. */
  padding-right: var(--v2026-rail-reserve);
}

.v2026-card-rail {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;                 /* <- full bleed. top/bottom 0 = the card's padding box */
  width: var(--v2026-rail-w);
  box-sizing: border-box;
  display: flex;
  align-items: center;       /* <- the icon centres against the CARD's full height */
  justify-content: flex-end;
  padding-left: 8px;         /* hairline -> button */
  padding-right: 9px;        /* button -> card border */
  border-left: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 0 11px 11px 0;   /* = the card's 12px minus its 1px border. The card has NO
                                     overflow:hidden, so without this the hover tint pokes square
                                     corners outside the card's rounded ones. */
  transition: background-color 0.12s ease;   /* just inside the card's own 0.15s box-shadow */
}

/* The WHOLE column is the button, so the hover spans it - in the INCOGNITO feature's own grey, not
   the brand cyan: #48484A is the incognito reading mode's header bar, so the hover previews where the
   click goes. Same token as the pill's background: 8% over 8% keeps the pill a visible step darker. */
.v2026-card-rail:hover  { background: var(--v2026-color-stealth-soft); }
.v2026-card-rail:active { background: rgba(72, 72, 74, 0.16); }  /* = --v2026-color-stealth @16% = 2x -soft */

/* The countdown strip bleeds -15px to reach the card's right edge, which assumed padding-right:15px.
   Re-widen it for the rail's reserved gutter so the strip renders exactly as it does today. */
.v2026-card--railed ::v-deep .v2026-cd-strip {
  margin-right: calc(-1 * var(--v2026-rail-reserve));
}

@media (max-width: 1200px) {
  /* 300px list rail. One variable moves; padding-right and the strip follow automatically. */
  .v2026-card--railed { --v2026-rail-w: 48px; }
  .v2026-card-rail { padding-left: 6px; }
}

/* ===== COMPACT CARD MODE (list column < 300px) ==========================================
   Driven by the `compact` prop; Conversation.vue measures .v2026-col-list. Every selector starts
   with .v2026-cards--compact, so normal cards never match and no rule above was edited.
   One class more specific than the base rules and the @media block, and last in the file.
   NOT @container: vue's scoped plugin does not enter @container (no data-v, lower specificity,
   ::v-deep left invalid), so the overrides would silently lose.
   MIRRORS - edit the compact twin when you edit any of these: .v2026-card, .v2026-card-content,
   .v2026-card--railed, .v2026-card-rail (+ @media 1200px), .v2026-card-stealth, .v2026-card-row1,
   -avatar, -headline, -metacol, -meta, -owner, -name-line, -name, -waiting, -preview-line,
   .v2026-channel-logo, .v2026-card-channel-badge, .v2026-card-tags(--chips), -tagchip, -resolvedchip, -reminder, -actions,
   -watching, .v2026-si-watermark; and in ReminderCountdownV2026: .v2026-cd-strip (+ enter/leave),
   .v2026-cd-watermark (+ ::before/::after), .v2026-card-reminder.
   No :hover box-shadow here: the rail-hover shadow reset above must keep winning. */

/* Frame. The padding shorthand outranks --railed's padding-right, so railed re-declares it; the
   reserve var itself is inherited from the base --railed rule and follows the two vars below. */
.v2026-cards--compact .v2026-card {
  padding: 12px;
}

.v2026-cards--compact .v2026-card--railed {
  --v2026-rail-w: 36px;      /* 1 border + 4 + 26 button + 5 */
  --v2026-rail-gap: 10px;
  padding-right: var(--v2026-rail-reserve);
}

/* Content-zone identity: all four values move together with the card padding, or hover gets a
   dead ring. Still no position / z-index / isolation / overflow here. */
.v2026-cards--compact .v2026-card-content {
  margin: -12px;
  margin-right: calc(-1 * var(--v2026-rail-gap, 12px));
  padding: 12px;
  padding-right: var(--v2026-rail-gap, 12px);
}

/* Beats the @media (max-width: 1200px) padding-left by the extra class. */
.v2026-cards--compact .v2026-card-rail {
  padding-left: 4px;
  padding-right: 5px;
}

/* โหมด compact ไม่ต้องขยับตำแหน่ง (เกาะมุมการ์ด ไม่ได้เกาะคอลัมน์ rail) แค่ย่อลงให้ได้สัดส่วนกับการ์ดที่เล็กลง */
.v2026-cards--compact .v2026-card-count {
  min-width: 20px;
  height: 20px;
  font-size: 11px;
  top: -5px;
  right: -5px;
}

.v2026-cards--compact .v2026-card-stealth {
  padding: 3px;              /* 3 + 20 mask + 3 = 26 */
}

/* Row 1: avatar spans the headline and meta rows. No DOM change, no display: contents. */
.v2026-cards--compact .v2026-card-row1 {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  column-gap: 10px;
  row-gap: 2px;
  align-items: start;
}

.v2026-cards--compact .v2026-card-avatar {
  grid-column: 1;
  grid-row: 1 / span 2;
  flex: none;
  width: 36px;
}

.v2026-cards--compact .v2026-card-avatar .image-cropper-md,
.v2026-cards--compact .v2026-card-avatar .image-profile {
  width: 36px;
  height: 36px;
}

.v2026-cards--compact .v2026-card-channel-badge {
  top: 26px;
  left: 26px;
}

.v2026-cards--compact .v2026-card-headline {
  grid-column: 2;
  grid-row: 1;
}

.v2026-cards--compact .v2026-card-metacol {
  grid-column: 2;
  grid-row: 2;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
}

.v2026-cards--compact .v2026-card-meta {
  min-height: 20px;
  gap: 6px;
}

.v2026-cards--compact .v2026-card-owner {
  width: 20px;
  height: 20px;
  border-width: 1.5px;
}

/* The only .badge inside the avatar is ด่วน!; pro badges live in the name line. */
.v2026-cards--compact .v2026-card-avatar .badge {
  font-size: 10px;
  line-height: 16px;
  padding: 0 4px;
}

/* VIP badge is v-html (unscoped) with an inline height, hence ::v-deep and !important. */
.v2026-cards--compact .v2026-card-avatar ::v-deep img:not(.image-profile):not(.v2026-card-channel-badge__img) {
  display: block;
  width: auto;
  max-width: 36px;
  height: auto !important;
  margin-top: 4px;
}

/* Name: one line; the pro badge never shrinks, only the name ellipsizes (title carries the full name). */
.v2026-cards--compact .v2026-card-name-line {
  flex-wrap: nowrap;
  min-width: 0;
}

.v2026-cards--compact .v2026-card-name-line .badge {
  flex: 0 0 auto;
}

.v2026-cards--compact .v2026-card-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 15px;
}

/* Waiting timer may break BETWEEN label and time, never inside either. A wrapping flex box, not
   white-space: normal - the template compiler drops the whitespace between the two spans, so inline
   text has no break opportunity there. Label keeps its 4px margin-right as the one-line spacing. */
.v2026-cards--compact .v2026-card-waiting {
  flex: 0 1 auto;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  white-space: normal;
}

.v2026-cards--compact .v2026-card-waiting-label,
.v2026-cards--compact .v2026-card-waiting-time {
  white-space: nowrap;
}

/* The 60px indents go to 0; top margins stay as they are. The child's bell needs ::v-deep. */
.v2026-cards--compact .v2026-card-preview-line,
.v2026-cards--compact .v2026-card-tags,
.v2026-cards--compact .v2026-card-reminder,
.v2026-cards--compact .v2026-card-actions {
  margin-left: 0;
}

.v2026-cards--compact ::v-deep .v2026-card-reminder {
  margin-left: 0;
}

.v2026-cards--compact .v2026-channel-logo {
  height: 20px;
}

.v2026-cards--compact .v2026-card-preview {
  min-width: 0;
}

/* Tag row is one line: the tag chip shrinks (label ellipsis), Resolved and +N do not. */
.v2026-cards--compact .v2026-card-tags--chips {
  flex-wrap: nowrap;
  min-width: 0;
}

.v2026-cards--compact .v2026-card-tagchip {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  padding: 5px 8px;
}

.v2026-cards--compact .v2026-card-resolvedchip {
  padding: 5px 8px;
}

/* Rollback path (useCardTagChips = false): badges wrap as today, a single long one ellipsizes. */
.v2026-cards--compact .v2026-card-tags:not(.v2026-card-tags--chips) .badge {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

/* row-reverse + wrap keeps every line right-aligned. */
.v2026-cards--compact .v2026-card-watching {
  flex-wrap: wrap;
}

/* Watermarks 104 -> 72 (x0.69). Both are clipped by their skins. */
.v2026-cards--compact .v2026-si-watermark {
  width: 72px;
  height: 72px;
  margin-top: -36px;
}

.v2026-cards--compact ::v-deep .v2026-cd-watermark {
  width: 72px;
  height: 72px;
  margin-top: -36px;
  border-width: 7px;
}

.v2026-cards--compact ::v-deep .v2026-cd-watermark::before,
.v2026-cards--compact ::v-deep .v2026-cd-watermark::after {
  width: 5px;
  margin-left: -2.5px;
  border-radius: 2.5px;
}

.v2026-cards--compact ::v-deep .v2026-cd-watermark::before {
  height: 22px;
}

.v2026-cards--compact ::v-deep .v2026-cd-watermark::after {
  height: 15px;
}

/* Countdown strip bleed. Longhands only: a margin shorthand would clobber the railed margin-right.
   margin-bottom stays the child's 12px. */
.v2026-cards--compact ::v-deep .v2026-cd-strip {
  margin-top: -12px;
  margin-left: -12px;
}

.v2026-cards--compact .v2026-card:not(.v2026-card--railed) ::v-deep .v2026-cd-strip {
  margin-right: -12px;
}

.v2026-cards--compact .v2026-card--railed ::v-deep .v2026-cd-strip {
  margin-right: calc(-1 * var(--v2026-rail-reserve));
}

/* The compact margin-top above outranks the child's enter/leave collapse; restore it, after it. */
.v2026-cards--compact ::v-deep .v2026-cd-strip-enter,
.v2026-cards--compact ::v-deep .v2026-cd-strip-leave-to {
  margin-top: 0;
  margin-bottom: 0;
}
</style>
